import { formatToISODate } from '../../helpers/date-time-helpers/formatting/formatting';
import type { ProcessScope } from '../../store/process-store/process-store.type';
import { FloraSupportedObjectTypes, type FloraContextObject, type FloraContextualInfo } from '../flora/flora.type';
import { GeneralViewNames } from '../side-bar/side-bar.type';

/**
 * Creates a Flora context object from a process scope
 *
 * @param {ProcessScope} scope - The process scope containing portfolio, team, and board information
 * @returns {FloraContextObject | undefined} A Flora context object or undefined if no valid scope object is found
 */

function getFloraContextObjectFromScope(scope: ProcessScope): FloraContextObject | undefined {
  if (scope.workPeriod) {
    return {
      type: FloraSupportedObjectTypes.WorkPeriod,
      id: scope.workPeriod.id,
      name: scope.workPeriod.name,
    };
  } else if (scope.board) {
    return {
      type: FloraSupportedObjectTypes.Subproject,
      id: scope.board.id,
      name: scope.board.name,
    };
  } else if (scope.team) {
    return {
      type: FloraSupportedObjectTypes.Project,
      id: scope.team.id,
      name: scope.team.name,
    };
  } else if (scope.portfolio) {
    return {
      type: FloraSupportedObjectTypes.Portfolio,
      id: scope.portfolio.id,
      name: scope.portfolio.name,
    };
  }
}

/**
 * Creates a Flora contextual info from process scopes
 *
 * @param {ProcessScope} left - The left process scope containing portfolio, team, and board information
 * @param {ProcessScope} right - The right process scope containing portfolio, team, and board information
 * @param {boolean} isComparisonView - Whether the view is a comparison view
 * @returns {FloraContextualInfo} A Flora contextual info with current objects and metadata
 */
function getProcessFloraContext(
  left: ProcessScope,
  right: ProcessScope,
  isComparisonView: boolean,
): FloraContextualInfo {
  const objects: FloraContextObject[] = [];

  const leftContextObject = getFloraContextObjectFromScope(left);
  const rightContextObject = getFloraContextObjectFromScope(right);
  if (leftContextObject) {
    objects.push(leftContextObject);
  }
  if (rightContextObject && isComparisonView) {
    objects.push(rightContextObject);
  }

  return {
    currentModule: GeneralViewNames.Process,
    currentObjects: objects,
    startDate: formatToISODate(left.startDate),
    endDate: formatToISODate(left.endDate),
  };
}

export { getProcessFloraContext };
